import React, {useEffect, useState} from "react";

/*
 * RangeSlider — dwuuchwytowy suwak zakresu
 * (Figma node 2013:6166)
 *
 * Props:
 *   items       — tablica dostępnych wartości (np. [500, 1000, 1500, ...])
 *   range       — { from: value, to: value }
 *   onChange    — callback({ from, to })
 *   unit        — string np. "zł", "km", "KM"
 *   placeholder — [labelOd, labelDo]
 */
function RangeSlider({items, range, onChange, unit, placeholder}) {
    const safeItems = items && items.length > 0 ? items : [0, 1];
    const maxIdx = safeItems.length - 1;

    const findIndex = (value) => {
        if (value === "" || value === null || value === undefined) return -1;
        const idx = safeItems.indexOf(value);
        if (idx !== -1) return idx;
        // nearest
        const num = Number(value);
        let nearest = 0;
        let minDiff = Math.abs(Number(safeItems[0]) - num);
        for (let i = 1; i < safeItems.length; i++) {
            const diff = Math.abs(Number(safeItems[i]) - num);
            if (diff < minDiff) { minDiff = diff; nearest = i; }
        }
        return nearest;
    };

    const initFrom = () => {
        const idx = findIndex(range && range.from);
        return idx !== -1 ? idx : 0;
    };
    const initTo = () => {
        const idx = findIndex(range && range.to);
        return idx !== -1 ? idx : (items && items.length > 0 ? maxIdx : 0);
    };

    const [fromIdx, setFromIdx] = useState(initFrom);
    const [toIdx, setToIdx] = useState(initTo);
    const [fromText, setFromText] = useState('');
    const [toText, setToText] = useState('');
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);

    // sync external range changes or items change
    useEffect(() => {
        const f = findIndex(range && range.from);
        const t = findIndex(range && range.to);

        let newFrom = f !== -1 ? f : 0;
        let newTo = (range && (range.to !== null && range.to !== undefined)) ? (t !== -1 ? t : maxIdx) : maxIdx;

        newFrom = Math.max(0, Math.min(newFrom, maxIdx));
        newTo = Math.max(0, Math.min(newTo, maxIdx));

        if (maxIdx > 0 && newFrom >= newTo) {
            newFrom = Math.max(0, newTo - 1);
        }

        setFromIdx(newFrom);
        setToIdx(newTo);

    }, [range && range.from, range && range.to, items]);

    const handleFromChange = (e) => {
        const val = Math.min(Number(e.target.value), toIdx > 0 ? toIdx - 1 : 0);
        setFromIdx(val);
        onChange({from: safeItems[val], to: safeItems[toIdx]});
    };

    const handleToChange = (e) => {
        const val = Math.max(Number(e.target.value), fromIdx > maxIdx - 1 ? maxIdx : fromIdx + 1);
        setToIdx(val);
        onChange({from: safeItems[fromIdx], to: safeItems[val]});
    };

    const commitFrom = (text) => {
        const num = parseInt(text.replace(/\D/g, ''), 10);
        if (!isNaN(num)) {
            let idx = findIndex(num);
            if (idx === -1) idx = 0;
            idx = Math.min(idx, toIdx > 0 ? toIdx - 1 : 0);
            idx = Math.max(0, Math.min(idx, maxIdx));
            setFromIdx(idx);
            onChange({from: safeItems[idx], to: safeItems[toIdx]});
        }
    };

    const commitTo = (text) => {
        const num = parseInt(text.replace(/\D/g, ''), 10);
        if (!isNaN(num)) {
            let idx = findIndex(num);
            if (idx === -1) idx = maxIdx;
            idx = Math.max(fromIdx < maxIdx ? fromIdx + 1 : maxIdx, idx);
            idx = Math.max(0, Math.min(idx, maxIdx));
            setToIdx(idx);
            onChange({from: safeItems[fromIdx], to: safeItems[idx]});
        }
    };

    const fromPercent = maxIdx > 0 ? (fromIdx / maxIdx) * 100 : 0;
    const toPercent = maxIdx > 0 && toIdx >= 0 ? (toIdx / maxIdx) * 100 : 0;

    const formatValue = (val) =>
        val !== undefined && val !== null && val !== "" && !isNaN(val)
            ? Number(val).toLocaleString("pl-PL")
            : "—";

    return (
        <div className="range-slider">
            {/* Etykiety z wartościami */}
            <div className="range-slider__labels">
                <div className="range-slider__label-group">
                    <span className="range-slider__label-text">{placeholder[0]}</span>
                    <input
                        type="text"
                        className="range-slider__label-input"
                        value={fromFocused ? fromText : formatValue(safeItems[fromIdx])}
                        onFocus={() => { setFromFocused(true); setFromText(String(safeItems[fromIdx])); }}
                        onChange={e => setFromText(e.target.value.replace(/[^\d]/g, ''))}
                        onBlur={() => { setFromFocused(false); commitFrom(fromText); }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                    {unit && <span className="range-slider__label-unit">{unit}</span>}
                </div>
                <div className="range-slider__label-group">
                    <span className="range-slider__label-text">{placeholder[1]}</span>
                    <input
                        type="text"
                        className="range-slider__label-input range-slider__label-input--active"
                        value={toFocused ? toText : formatValue(safeItems[toIdx])}
                        onFocus={() => { setToFocused(true); setToText(String(safeItems[toIdx])); }}
                        onChange={e => setToText(e.target.value.replace(/[^\d]/g, ''))}
                        onBlur={() => { setToFocused(false); commitTo(toText); }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                    {unit && <span className="range-slider__label-unit">{unit}</span>}
                </div>
            </div>

            {/* Track + suwaki */}
            <div className="range-slider__wrap">
                {/* Tło toru */}
                <div className="range-slider__track">
                    {/* Aktywne wypełnienie między uchwytami */}
                    <div
                        className="range-slider__fill"
                        style={{left: fromPercent + "%", width: (toPercent - fromPercent) + "%"}}
                    ></div>
                </div>

                {/* Ikonka lewego uchwytu (logo_icon, strzałka w prawo) */}
                <div
                    className="range-slider__thumb range-slider__thumb--from"
                    style={{left: fromPercent + "%"}}
                />

                {/* Ikonka prawego uchwytu (logo_icon obrócona 180°) */}
                <div
                    className="range-slider__thumb range-slider__thumb--to"
                    style={{left: toPercent + "%"}}
                />

                {/* Uchwyt "od" */}
                <input
                    type="range"
                    className="range-slider__input range-slider__input--from"
                    min={0}
                    max={maxIdx}
                    step={1}
                    value={fromIdx}
                    onChange={handleFromChange}
                />

                {/* Uchwyt "do" */}
                <input
                    type="range"
                    className="range-slider__input range-slider__input--to"
                    min={0}
                    max={maxIdx}
                    step={1}
                    value={toIdx}
                    onChange={handleToChange}
                />
            </div>
        </div>
    );
}

export default RangeSlider;
